import { useWeb3React } from '@web3-react/core'
import { useEffect, useState, useRef } from 'react'
import { handleChainChange } from '../functions'
import settings from '../settings'
import { getCurrentMintCount, presaleMint, getSignMessage, verifySignature, parseLogs } from './functions'


const SignStep = ({ setStep, setSignState }) => {
  const { account, library, chainId } = useWeb3React()

  useEffect(() => {
    if (settings.enableSwitchingNetwork && chainId && chainId !== 1) {
      handleChainChange()
    }
  }, [chainId])

  async function signClick(ev) {
    const acc = String(account)
    const { message } = await getSignMessage(acc)
    const signature = await library.getSigner(acc).signMessage(message)

    ev.preventDefault();

    try {
      const { status, nonce, proof, allowance } = await verifySignature(acc, signature)

      if (status !== 'ok') {
        throw new Error(`Invalid signature - ${status}`)
      }

      setSignState({
        signature,
        nonce,
        proof,
        allowance,
      })
      setStep('mint')
    }
    catch (e) {
      console.error(e)
    }
  }

  return (
    <>
      <div className="flex flex-col justify-center text-center" style={{
        minWidth: '50vw'
      }}>
        Hey Boomer!
        <br/>
        <br/>
        Welcome to our presale! First we need to verify your wallet.
        <br />
        <br/>
        <a className="theme-btn mb-4" onClick={signClick}>Click to sign login message</a>
      </div>
    </>
  )
}

const MintStep = ({ setStep, signState, setMintState }) => {
  const { account, library } = useWeb3React()

  const [balance, setBalance] = useState(undefined)
  const [message, setMessage] = useState()
  const numberOfTokensRef = useRef(null)

  async function mint() {
    const acc = String(account)
    const { nonce, proof, allowance } = signState

    const numberOfTokens = Number(numberOfTokensRef.current.value)

    setMessage(undefined)

    try {
      const tx = await presaleMint(library, acc, numberOfTokens, allowance, nonce, proof)

      setMintState({
        tx
      })
      setStep('final')
    }
    catch (e) {
      // e.code === 4001 - User denied transaction signature.
      console.error(e)

      if (e.code === 4001) {
        if (message !== undefined) {
          setMessage(undefined)
        }
      } else if (e.code === 'INSUFFICIENT_FUNDS') {
        setMessage(['red', 'insufficient funds for transaction cost']) //  (gas * price + value)
      } else {
        const errorDetailIndex = e.message.indexOf(' (error=')
        if (errorDetailIndex !== -1) {
          setMessage(['red', e.message.substring(0, errorDetailIndex)])
        } else {
          setMessage(['red', e.message])
        }
      }
    }
  }

  useEffect(() => {
    async function getBalance() {
      const acc = String(account)
      const { allowance } = signState

      const used = await getCurrentMintCount(library, acc)

      setBalance({
        allowance: allowance,
        used: used,
        remaining: allowance - used,
      })
    }

    getBalance()
  }, [account, library, signState])

  function Picker(props) {
    if (!props.balance) {
      return null
    }

    if (props.balance.remaining < 1) {
      return (
        <div className={'font-sans text-2xl my-8'}>You've already used all your tokens.</div>
      )
    }

    return (
      <>
        Available: {props.balance.remaining} / {props.balance.allowance}
        <br />
        <br />
        Number of Tokens: <select ref={numberOfTokensRef}>
          {new Array(props.balance.remaining).fill(1).map((_, i) => (
            <option key={i} value={i + 1}>{i + 1}</option>
          ))}
        </select>
        <br />
        <br />
        {message && (
          <>
            <div style={{ color: message[0] }}>{message[1]}</div>
            <br />
            <br />
          </>
        )}
        <button className={'theme-btn btn-big w-100'} onClick={() => mint()}>
          Mint
        </button>
      </>
    )
  }

  return (
    <>
      <div className="flex flex-col justify-center text-center w-full" style={{
        minWidth: '50vw'
      }}>
        <div className={'font-sans text-2xl my-8'}>
          Hey Boomer!
          <br/>
          <Picker balance={balance} />
        </div>
      </div>
    </>
  )
}

const FinalStep = ({ setStep, signState, mintState }) => {
  const { account, library } = useWeb3React()

  const [ mintedTokens, setMintedTokens ] = useState()

  useEffect(() => {
    async function getLogs() {
      const { tx } = mintState

      const receipt = await tx.wait()

      if (settings.env === 'development') {
        window.lastReceipt = receipt
      }

      const receiptLogs = parseLogs(library, receipt)

      if (settings.env === 'development') {
        window.lastReceiptLogs = receiptLogs
      }

      const tokenIds = receiptLogs
        // .filter(log => log.event === 'Transfer')
        .map(receiptLog => receiptLog.args[2].toString())

      setMintedTokens(tokenIds)
    }

    getLogs()
  }, [account, library, mintState])

  const txHash = mintState.tx.hash
  const txUrl = `${settings.contractEtherscanUrl}/tx/${txHash}`

  const tokenUrls = mintedTokens && mintedTokens.map(
    tokenId => [
      tokenId,
      `${settings.contractOpenseaUrl}/assets/${settings.contractAddress}/${tokenId}`,
      `${settings.contractRaribleUrl}/token/${settings.contractAddress}:${tokenId}`
    ]
  )

  return (
    <>
      <div className="flex flex-col justify-center text-center w-full" style={{
        minWidth: '50vw'
      }}>
        <h2 className={'text-5xl font-kang'}>Presale</h2>
        <div className={'font-sans text-2xl my-8'}>
          {tokenUrls && <>
            Thanks Boomer!
            <img src="/img/boomerKonfetti.gif" alt="" width={300} className={'mx-auto'}/>
          </>}
          <br/>
          <br />
          <div>
            <a href={txUrl} target={'_blank'} rel="noreferrer">View transaction {txHash.substring(0, 8)}... on Etherscan</a>
          </div>
          <br />
          {(tokenUrls ?? []).map(([tokenId, openseaUrl, raribleUrl]) => (
            <div key={tokenId} className={'mb-4'}>
              Successfully minted #{tokenId}
              {' '}
              <br/>
              <br/>
              <div className="d-flex justify-center w-100">
                <a href={openseaUrl} target={'_blank'} rel="noreferrer" className={'theme-btn mr-2'}>OpenSea</a>
                <a href={raribleUrl} target={'_blank'} rel="noreferrer" className={'theme-btn'}>Rarible</a>
              </div>
            </div>
          ))}
          {!tokenUrls && <div>Waiting for transaction to be completed...</div>}
        </div>
      </div>
    </>
  )
}

export default function PresaleWizard () {
  const [step, setStep] = useState('sign')
  const [signState, setSignState] = useState(undefined)
  const [mintState, setMintState] = useState(undefined)

  const steps = [
    ['sign', <SignStep setStep={setStep} setSignState={setSignState}/>],
    ['mint', <MintStep setStep={setStep} signState={signState} setMintState={setMintState}/>],
    ['final', <FinalStep setStep={setStep} signState={signState} mintState={mintState}/>],
  ]

  return steps.find(s => s[0] === step)[1]
}
